import React, { useCallback, useState, useEffect, useRef } from "react"
import { Row, Col, Flex, Button, Tag, Typography } from "antd"
import { CheckOutlined, DownloadOutlined, ShareAltOutlined } from "@ant-design/icons"
import { Icon } from "ui/Icon"
import Calendar from "../../../assets/icons/calendar.svg"
import AddressIcon from "../../../assets/icons/Address.svg"
import styles from "./index.module.css"
import { useBookingData } from "./hooks/useBookingData"
import { useTripStatus } from "./hooks/useTripStatus"
import BookingDetailsCard from "./components/BookingDetailsCard"
import GuideInformationCard from "./components/GuideInformationCard"
import PaymentCard from "./components/PaymentCard"
import AboutTripCard from "./components/AboutTripCard"
import MembersCard from "./components/MembersCard"
import TripDetailsCard from "./components/TripDetailsCard"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useDispatch, useSelector } from "react-redux"
import { showModal } from "modules/modals/reducer"
import { routes } from "router"
import ShareWaiver from "pagesV2/pro/Embedded/BookingConfirmation/modal/ShareWaiver"
import ParticipatingModal from "pagesV2/pro/Embedded/BookingConfirmation/modal/Participating"
import { useNavigate } from "react-router-dom"
import { getWaiverSubmissionsByTripBooking, checkSubmissionsAvailable } from "store/digital-waiver"
import { SignDocumentModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/SignDocument"
const { Title } = Typography

// Fallback data for development/testing when real data is not available
const FALLBACK_GUESTS = [
  { name: "Chris Anderson (you)", type: "Adult", waiver: "Uploaded", questionnaire: "Completed", avatar: "", main: true },
  { name: "Guest 2", type: "Adult", waiver: "Not signed", questionnaire: "Not completed", avatar: "" },
  { name: "Guest 3", type: "Child", waiver: "Not signed", questionnaire: "Not completed", avatar: "" }
]

const FALLBACK_EQUIPMENT = {
  name: "Boston Whaler 405 Conquest",
  image: "",
  description:
    "2023 Sportsman Masters OE 247 with a 300 Yamaha. Six seats spread out on the boat so you can relax when not fishing. Climb up on the 2nd station for a great birds eye view. The boat will be clean and ready for your next adventure!"
}

const FALLBACK_BOOKING_INSTRUCTIONS = "When you arrive I will be on the right of the boat dock."

export const TripsPreview = ({ isPublic = false }) => {
  const [showShareWaiver, setShowShareWaiver] = useState(false)
  const [showParticipatingModal, setShowParticipatingModal] = useState(false)
  const [isShowSignForChildWaiver, setIsShowSignForChildWaiver] = useState(false)
  const [pendingWaiverSubmission, setPendingWaiverSubmission] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  // const [totalDocumentCount,setTotalDocumentCount] = useState(0)

  const navigate = useNavigate()
  const location = window.location
  // Extract data using custom hooks
  const { trip, booking, guide, paymentSummary, paymentHistory, childCount, paymentMethod, totalAmount, hasWaivers, paymentStatus } = useBookingData()
  const { isCompleted, isBooked } = useTripStatus(trip, booking)
  const dispatch = useDispatch()
  const { id, first_name, last_name } = useSelector((state) => state.user)
  const fullName = `${first_name} ${last_name}`
  const {
    guest_data,
    loading,
    booking_questionnaire,
    share_waiver_allowed,
    guest_submission
  } = useSelector((state) => state.digitalWaiver.waiverSubmissionsByTripBooking)
  const { loading: waiverSubmissionLoading } = useSelector((state) => state.digitalWaiver.currentWaiverSubmission)
  const prevWaiverLoadingRef = useRef(false)

  const totalGuests = [
    ...guest_data
    // ...Object.values(shared_waivers_without_submissions || {}),
    // ...Object.values(shared_waivers_submissions || {})
  ]

  // Extract trip properties
  const {
    title,
    description,
    address,
    alcohol_allowed,
    accessibility_enabled,
    children_allowed,
    bring_items,
    included_items,
    skill_levels,
    boats
  } = trip || {}
  const bookingReceipt = booking.id || !trip.existing_draft_booking ? booking : trip.existing_draft_booking
  // Memoized event handlers
  const handleBack = useCallback(() => {
    navigate(-1)
    // Add navigation logic here
  }, [navigate])

  const handleShareReservation = useCallback(() => {
    dispatch(
      showModal("shareModal", {
        name: "Trip",
        path: routes.publicBookingPath({ uuid: booking?.uuid }),
        description,
        title
      })
    )
  }, [dispatch, booking?.uuid, description, title])

  const handleDownloadReceipt = useCallback(() => {
    window.open(bookingReceipt.receipt_url, "_blank")
    // Add download logic here
  }, [])

  const handleInviteWaiver = useCallback(() => {
    setShowShareWaiver(true)
    // Add invite waiver logic here
  }, [])

  const handleSignChildWaiver = useCallback(() => {
    setShowParticipatingModal(true)
    if (booking?.uuid) {
      dispatch(checkSubmissionsAvailable(booking.uuid))
    }
  }, [booking?.uuid, dispatch])

  const handleAddGuest = useCallback(() => {
    // Add guest logic here
  }, [])
  const handleCloseShareWaiver = useCallback(() => {
    setShowShareWaiver(false)
  }, [])
  // Refresh waivers when returning from signing (skip for public pages)
  useEffect(() => {
    if (!isPublic && booking?.uuid && hasWaivers !== false) {
      const guestToken = localStorage.getItem("guest_token") || undefined
      dispatch(getWaiverSubmissionsByTripBooking(booking.uuid, guestToken))
    }
  }, [isPublic, booking?.uuid, dispatch, refreshKey, location.hash])

  // After getWaiverSubmissionById (client/waiver_submissions/:id) completes, refresh the list
  useEffect(() => {
    if (prevWaiverLoadingRef.current && !waiverSubmissionLoading && !isPublic && booking?.uuid) {
      dispatch(getWaiverSubmissionsByTripBooking(booking.uuid))
    }
    prevWaiverLoadingRef.current = waiverSubmissionLoading
  }, [waiverSubmissionLoading, isPublic, booking?.uuid, dispatch])

  // Use fallback data when real data is not available
  const guests = totalGuests?.length > 0 ? totalGuests : FALLBACK_GUESTS
  const equipment = boats || FALLBACK_EQUIPMENT
  const meetingInstructions = booking?.meeting_instructions || FALLBACK_BOOKING_INSTRUCTIONS
const pendingDocumentSubmission = guest_data[0]?.pending_submission
  const hasGuestToken = !!localStorage.getItem("guest_token")
  return (
    <FirstLoading name="trips.trip">
      <div className={styles.root}>
        {!hasGuestToken && (
          <Flex align="center" gap={4} className="mb-4xl" style={{ cursor: "pointer" }}>
            <div className={styles.iconWrapper}>
              <Icon onClick={handleBack} width={16} height={16} type="feather" name="ChevronLeft" color="currentColor" />
            </div>
            <p className="mb-0" level={4}>
              Back
            </p>
          </Flex>
        )}

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Flex justify="space-between" align="center" gap={8} className="mb-xl">
              <div>
                <Title level={2} className={`${styles.title} mb-l`}>
                  {title}
                </Title>
                <Flex align="center" gap={24}>
                  {isCompleted && (
                    <Tag className={`${styles.tagCompleted} py-xxs px-s mr-0`}>
                      <CheckOutlined /> Completed
                    </Tag>
                  )}
                  {isBooked && (
                    <Tag className={styles.tagBooked}>
                      <CheckOutlined /> Booked
                    </Tag>
                  )}

                  <Flex align="center" gap={6}>
                    <img className={`${styles.icon}`} src={Calendar} alt="Date" />
                    <span className={`${styles.time} fw-500 color-text-secondary-dark`}>
                      {booking?.start_time && booking?.end_time ? `${booking?.start_time}-${booking?.end_time}` : "-"}
                    </span>
                  </Flex>
                  <Flex align="center" gap={6}>
                    <img className={`${styles.icon}`} src={AddressIcon} alt="Address" />
                    <span className={`${styles.address} fw-500 color-text-secondary-dark`}>{address}</span>
                  </Flex>
                </Flex>
              </div>
              <Flex gap={12}>
                <Button color="primary" variant="outlined" icon={<ShareAltOutlined />} type="default" onClick={handleShareReservation}>
                  Share reservation
                </Button>
                <Button icon={<DownloadOutlined />} color="primary" variant="outlined" onClick={handleDownloadReceipt}>
                  Download receipt
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>

        <Row gutter={[22]}>
          <Col xs={24} lg={12}>
            <BookingDetailsCard booking={booking} trip={trip} meetingInstructions={meetingInstructions} />
            {hasWaivers !== false && <GuideInformationCard guide={guide} />}
            {!isPublic && (
              <PaymentCard
                totalAmount={totalAmount}
                paymentMethod={paymentMethod}
                paymentSummary={paymentSummary}
                paymentHistory={paymentHistory}
                paymentStatus={paymentStatus}
                pricing={trip?.pricing}
              />
            )}
            <AboutTripCard description={description} boat={equipment} />
          </Col>
          <Col xs={24} lg={12}>
            {hasWaivers === false && <GuideInformationCard guide={guide} />}
            {!isPublic && hasWaivers !== false && (
              <MembersCard
                isSharedWaiverAllowed={share_waiver_allowed}
                setPendingWaiverSubmission={setPendingWaiverSubmission}
                setIsShowSignForChildWaiver={setIsShowSignForChildWaiver}
                uuid={booking?.uuid}
                isShowGuestSubmission={guest_submission}
                booking_questionnaire={booking_questionnaire}
                fullName={fullName}
                loading={loading}
                guests={guests}
                onInviteWaiver={handleInviteWaiver}
                onSignChildWaiver={handleSignChildWaiver}
                onAddGuest={handleAddGuest}
                childCount={childCount}
              />
            )}
            <TripDetailsCard
              includedItems={included_items}
              bringItems={bring_items}
              skillLevels={skill_levels}
              alcoholAllowed={alcohol_allowed}
              accessibilityEnabled={accessibility_enabled}
              childrenAllowed={children_allowed}
            />
            {isPublic && (
              <PaymentCard
                totalAmount={totalAmount}
                paymentMethod={paymentMethod}
                paymentSummary={paymentSummary}
                paymentHistory={paymentHistory}
                paymentStatus={paymentStatus}
                pricing={trip?.pricing}
              />
            )}
          </Col>
        </Row>
      </div>
      <ShareWaiver uuid={booking.uuid} visible={showShareWaiver} onClose={handleCloseShareWaiver} id={booking.id} />

      {(showParticipatingModal || isShowSignForChildWaiver) && (
        <ParticipatingModal
          childCount={childCount}
          uuid={booking.uuid}
          isShowSignForChildWaiver={isShowSignForChildWaiver}
          setShowSignForChildWaiver={setIsShowSignForChildWaiver}
          userId={booking?.client?.id || id}
          bookingWaivers={booking.booking_waivers}
          tripBookingId={booking.id}
          sharedWaiverId={null}
          visible={showParticipatingModal || isShowSignForChildWaiver}
          onClose={() => {
            setShowParticipatingModal(false)
            setIsShowSignForChildWaiver(false)
          }}
          // onNext={() => {setShowParticipatingModal(false); setIsShowSignForChildWaiver(false);}}
        />
      )}
      {((pendingDocumentSubmission?.length > 0 && showParticipatingModal) || (pendingWaiverSubmission?.length > 0 && isShowSignForChildWaiver)) && (
        <SignDocumentModal
          uuid={booking.uuid}
          pendingSubmission={pendingWaiverSubmission || pendingDocumentSubmission}
          waiverSubmissionId={(pendingWaiverSubmission || pendingDocumentSubmission)?.[0]?.waiver_submission_id}
          bookingWaivers={booking.booking_waivers}
          id={booking?.client?.id || id}
          selected={pendingWaiverSubmission?.[0]?.waiver_id}
          waiver={booking.booking_waivers.find((w) => w.waiver_id === pendingWaiverSubmission?.[0]?.waiver_id)}
          loading={loading}
          open={showParticipatingModal || isShowSignForChildWaiver}
          onCancel={() => {
            setShowParticipatingModal(false)
            setIsShowSignForChildWaiver(false)
            setRefreshKey(prev => prev + 1)
          }}
        />
      )}
    </FirstLoading>
  )
}
